import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Avatar,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import PlantSearch from '../components/PlantSearch';
import { offlineApiService } from '../services/offlineApi';
import toast from 'react-hot-toast';

const steps = ['Select Plant', 'Plant Details', 'Location & Care'];

const AddPlant: React.FC = () => {
  const navigate = useNavigate();
  const { farmId } = useParams();
  const [searchParams] = useSearchParams();
  const gardenId = searchParams.get('garden');
  const spaceId = searchParams.get('space');
  const areaId = searchParams.get('area');

  const [activeStep, setActiveStep] = useState(0);
  const [selectedPlant, setSelectedPlant] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Form data
  const [plantData, setPlantData] = useState({
    name: '',
    variety: '',
    locationDescription: '',
    garden: gardenId || '',
    space: spaceId || '',
    area: areaId || '',
    stage: 'planning',
    health: 'good',
    isPublic: true,
    care: {
      wateringSchedule: 'as-needed',
      sunlightRequirement: 'partial-sun'
    },
    notes: ''
  });

  // Load location data
  const [gardens, setGardens] = useState<any[]>([]);
  const [spaces, setSpaces] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);

  useEffect(() => {
    loadLocationData();
  }, [farmId]);

  const loadLocationData = async () => {
    try {
      // Load gardens
      const gardensResponse = await offlineApiService.get(`/farms/${farmId}/gardens`);
      setGardens(gardensResponse.data);

      // Load spaces if available
      if (farmId) {
        const spacesResponse = await offlineApiService.get(`/farms/${farmId}/spaces`);
        setSpaces(spacesResponse.data);
      }
    } catch (error) {
      console.error('Error loading location data:', error);
    }
  };

  const handleSelectPlant = (plant: any) => {
    setSelectedPlant(plant);
    setPlantData({
      ...plantData,
      name: plant.commonName,
      variety: plant.scientificName,
      care: {
        ...plantData.care,
        wateringSchedule: mapWaterToSchedule(plant.growingRequirements.water),
        sunlightRequirement: plant.growingRequirements.sunlight[0] || 'partial-sun'
      }
    });
    setActiveStep(1);
  };

  const mapWaterToSchedule = (water: string) => {
    const scheduleMap: { [key: string]: string } = {
      'low': 'weekly',
      'moderate': 'every-other-day',
      'high': 'daily',
      'very-high': 'twice-daily'
    };
    return scheduleMap[water] || 'as-needed';
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const plantPayload = {
        ...plantData,
        plantDatabaseId: selectedPlant._id,
        scientificName: selectedPlant.scientificName,
        plantType: selectedPlant.plantType,
        images: selectedPlant.images,
        farm: farmId
      };

      await offlineApiService.post('/plants', plantPayload);
      
      toast.success('Plant added successfully! +10 XP');
      navigate(`/farms/${farmId}/manage`);
    } catch (error) {
      console.error('Error adding plant:', error);
      toast.error('Failed to add plant');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search for Your Plant
              </Typography>
              <PlantSearch
                onSelectPlant={handleSelectPlant}
                filters={{
                  indoorOnly: gardens.find(g => g._id === plantData.garden)?.type === 'indoor'
                }}
              />
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plant Details
              </Typography>
              
              {selectedPlant && (
                <Box mb={3}>
                  <Alert severity="info">
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={selectedPlant.images[0]?.url}
                        sx={{ width: 40, height: 40 }}
                      >
                        <LocalFloristIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          Selected: {selectedPlant.commonName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedPlant.scientificName}
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Plant Name"
                    value={plantData.name}
                    onChange={(e) => setPlantData({ ...plantData, name: e.target.value })}
                    helperText="Give your plant a unique name"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Variety"
                    value={plantData.variety}
                    onChange={(e) => setPlantData({ ...plantData, variety: e.target.value })}
                    helperText="Specific variety or cultivar"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Growth Stage</InputLabel>
                    <Select
                      value={plantData.stage}
                      onChange={(e) => setPlantData({ ...plantData, stage: e.target.value })}
                      label="Growth Stage"
                    >
                      <MenuItem value="planning">Planning</MenuItem>
                      <MenuItem value="seed">Seed</MenuItem>
                      <MenuItem value="germinating">Germinating</MenuItem>
                      <MenuItem value="seedling">Seedling</MenuItem>
                      <MenuItem value="vegetative">Vegetative</MenuItem>
                      <MenuItem value="flowering">Flowering</MenuItem>
                      <MenuItem value="fruiting">Fruiting</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Health Status</InputLabel>
                    <Select
                      value={plantData.health}
                      onChange={(e) => setPlantData({ ...plantData, health: e.target.value })}
                      label="Health Status"
                    >
                      <MenuItem value="excellent">Excellent</MenuItem>
                      <MenuItem value="good">Good</MenuItem>
                      <MenuItem value="fair">Fair</MenuItem>
                      <MenuItem value="poor">Poor</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={plantData.isPublic}
                        onChange={(e) => setPlantData({ ...plantData, isPublic: e.target.checked })}
                      />
                    }
                    label="Make this plant visible to the community"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Location & Care
              </Typography>

              <Grid container spacing={2}>
                {plantData.garden && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Garden</InputLabel>
                      <Select
                        value={plantData.garden}
                        onChange={(e) => setPlantData({ ...plantData, garden: e.target.value })}
                        label="Garden"
                      >
                        {gardens.map(garden => (
                          <MenuItem key={garden._id} value={garden._id}>
                            {garden.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {spaces.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Space (Optional)</InputLabel>
                      <Select
                        value={plantData.space}
                        onChange={(e) => setPlantData({ ...plantData, space: e.target.value })}
                        label="Space (Optional)"
                      >
                        <MenuItem value="">None</MenuItem>
                        {spaces.map(space => (
                          <MenuItem key={space._id} value={space._id}>
                            {space.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location Description"
                    value={plantData.locationDescription}
                    onChange={(e) => setPlantData({ ...plantData, locationDescription: e.target.value })}
                    multiline
                    rows={2}
                    helperText="Where exactly is this plant located?"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Watering Schedule</InputLabel>
                    <Select
                      value={plantData.care.wateringSchedule}
                      onChange={(e) => setPlantData({ 
                        ...plantData, 
                        care: { ...plantData.care, wateringSchedule: e.target.value }
                      })}
                      label="Watering Schedule"
                    >
                      <MenuItem value="twice-daily">Twice Daily</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="every-other-day">Every Other Day</MenuItem>
                      <MenuItem value="twice-weekly">Twice Weekly</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                      <MenuItem value="as-needed">As Needed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sunlight Requirement</InputLabel>
                    <Select
                      value={plantData.care.sunlightRequirement}
                      onChange={(e) => setPlantData({ 
                        ...plantData, 
                        care: { ...plantData.care, sunlightRequirement: e.target.value }
                      })}
                      label="Sunlight Requirement"
                    >
                      <MenuItem value="full-sun">Full Sun (6+ hours)</MenuItem>
                      <MenuItem value="partial-sun">Partial Sun (3-6 hours)</MenuItem>
                      <MenuItem value="partial-shade">Partial Shade (2-3 hours)</MenuItem>
                      <MenuItem value="full-shade">Full Shade (&lt; 2 hours)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={plantData.notes}
                    onChange={(e) => setPlantData({ ...plantData, notes: e.target.value })}
                    multiline
                    rows={3}
                    helperText="Any additional notes about this plant"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 10 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', pt: 2, pb: 3 }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ color: 'white' }}
            >
              Back
            </Button>
            <Typography variant="h5" flex={1}>
              Add New Plant
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        {getStepContent(activeStep)}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || (activeStep === 0 && !selectedPlant)}
          >
            {activeStep === steps.length - 1 ? 'Add Plant' : 'Next'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default AddPlant;